import React from 'react';
import type { SortDescriptor } from '@react-types/shared';
import type { BlockedUserItem } from '@/types';

export function useBlockedUsersSorting(blockedUsers: BlockedUserItem[]) {
  const [sortDescriptor, setSortDescriptor] = React.useState<SortDescriptor | undefined>(undefined);

  const sortedBlockedUsers = React.useMemo(() => {
    if (!sortDescriptor?.column) return blockedUsers;

    const copy = [...blockedUsers];
    const { column, direction } = sortDescriptor;

    const compareStrings = (a: string, b: string) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' });
    const compareNumbers = (a: number, b: number) => a - b;
    const compareBool = (a: boolean, b: boolean) => (a === b ? 0 : a ? 1 : -1);

    copy.sort((a, b) => {
      let cmp = 0;

      switch (column) {
        case 'id':
          cmp = compareNumbers(a.id, b.id);
          break;
        case 'domain':
          cmp = compareStrings(a.domain?.domain ?? '', b.domain?.domain ?? '');
          break;
        case 'fingerprint':
          cmp = compareStrings(a.fingerprint, b.fingerprint);
          break;
        case 'reason':
          cmp = compareStrings(a.reason ?? '', b.reason ?? '');
          break;
        case 'ip':
          cmp = compareStrings(a.ip ?? '', b.ip ?? '');
          break;
        case 'blocked_at':
          cmp = compareStrings(a.blocked_at, b.blocked_at);
          break;
        case 'status':
          cmp = compareBool(a.is_blocked, b.is_blocked);
          break;
        default:
          cmp = 0;
      }

      return direction === 'descending' ? -cmp : cmp;
    });

    return copy;
  }, [blockedUsers, sortDescriptor]);

  return { sortDescriptor, setSortDescriptor, sortedBlockedUsers };
}
